import React, { ChangeEvent, ReactNode, useEffect, useRef, useState } from "react";
import { Box, Button, DialogActions, DialogContent, DialogTitle, Divider, Pagination, Stack, Typography, useTheme } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

import ResponsiveDialog from "components/dialogs/ResponsiveDialog";
import { ApiPagination, Tag } from "./TagManagementPage";
import APIRequest from "system/APIRequest";
import Chip from "components/uiElements/dataDisplay/Chip";
import { Query } from "system/query/Query";
import { QueryInfo } from "system/query/QueryInfo";
import SavedQueryRow from "components/queries/savedQueries/SavedQueryRow";
import ConfirmationDialog from "components/dialogs/ConfirmationDialog";
import TagRequestHelper from "./TagRequestHelper";
import { useTranslation } from "react-i18next";

interface TagQueriesDialogProps {
  onClose(): void;
  onTagRemovedFromQuery(): void;
  tag: Tag;
}

export interface ResourceTagEntry {
  id: string;
  resource_id: string;
  resource_tag_id: string;
  resource_type: string;
}

const PaginationStart: ApiPagination = {
  cursor: "",
  page: 1,
  per_page: 10,
  total: 0
};

export default function TagQueriesDialog(props: TagQueriesDialogProps): React.JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const [resourceTagEntries, setResourceTagEntries] = useState<Array<ResourceTagEntry>>([]);
  const [queries, setQueries] = useState<Array<Query>>([]);
  const [queryInfos, setQueryInfos] = useState<Record<string, QueryInfo>>({});
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [pagination, setPagination] = useState<ApiPagination>({...PaginationStart});

  const selectedQuery = useRef<Query | null>(null);

  async function loadTagQueries() {
    const result = await TagRequestHelper.getQueriesForTag(props.tag.id, pagination);
    setPagination(result.pagination);
    setResourceTagEntries(result.resourceTagEntries);
    setQueries(result.queries);
  }

  async function loadQueryInfos() {
    if (queries.length === 0) {
      setQueryInfos({});
      return;
    }
    const datasetIdSet = new Set<string>();
    for (const query of queries) {
      datasetIdSet.add(query.dataset_id);
    }
    const datasetIds = [...datasetIdSet];
    const requests: Array<Promise<QueryInfo>> = datasetIds.map((id) => {
      return new Promise<QueryInfo>(async (resolve, reject) => {
        const response: QueryInfo = await APIRequest(`query-info/${id}`);
        resolve(response);
      });
    });
    const responses = await Promise.all(requests);
    const newQueryInfos: Record<string, QueryInfo> = {};
    for (let i=0; i<datasetIds.length; i++) {
      newQueryInfos[datasetIds[i]] = responses[i];
    }
    setQueryInfos(newQueryInfos);
  }

  async function removeTagFromQuery(query: Query) {
    const resourceTagEntry = resourceTagEntries.find((entry) => entry.resource_id == query.id);
    if (!resourceTagEntry) {
      return;
    }

    const response = await APIRequest(`obj/resource-tag-entry/${resourceTagEntry?.id}`, {}, {
      method: "DELETE"
    });

    const updatedQueries = queries.filter((current) => current.id !== query.id);
    setQueries(updatedQueries);

    props.onTagRemovedFromQuery();
  }

  useEffect(() => {
    loadTagQueries();
  }, [pagination.page]);

  useEffect(() => {
    loadQueryInfos();
  }, [queries]);

  function handlePageChanged(event: ChangeEvent<unknown>, value: number): void {
    setPagination({...pagination, page: value});
  }

  const tagQueryRow: ReactNode = queries.map((query, index) => {
    const queryInfo = queryInfos[query.dataset_id];
    return (
      <Stack key={`TagUsageDialog.${query.id}`} direction="row" sx={{
        padding: 1
      }}>
        <Box sx={{
          flex: 1
        }}>
          <SavedQueryRow
            savedQuery={query}
            queryInfo={queryInfo}
          />
        </Box>
        <Box sx={{
          marginLeft: 1
        }}>
          <Button
            color="error"
            onClick={() => {
              selectedQuery.current = query;
              setShowDeleteConfirmation(true);
            }}
            size="small"
            variant="contained"
          >
            {i18n.t("tag-queries-dialog.remove-label")}
          </Button>
        </Box>
      </Stack>
    );
  });
  
  return (
    <ResponsiveDialog maxWidth="md" open={true}>
      <DialogTitle>
        <Stack direction="row" sx={{
          alignItems: "baseline"
        }}>
          <Chip color={props.tag.color} label={props.tag.name} />
          <Typography
            variant="h4"
            sx={{
              flex: 1,
              marginLeft: 0.5
            }}>{i18n.t("tag-queries-dialog.title")}</Typography>
          <Button 
            color="secondary"
            onClick={(event) => {
              event.stopPropagation();
              props.onClose();
            }}
            sx={{minWidth: 0, padding: 0}}
          >
            <CloseIcon />
          </Button>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack
          divider={<Divider />}
          sx={{
            border: 1,
            borderColor: theme.palette.grey[300],
            borderRadius: theme.general.borderRadius
          }}>
          {tagQueryRow}
        </Stack>
        {pagination.total > pagination.per_page &&
          <Stack
            sx={{
              alignItems: "center",
              padding: 1
            }}>
            <Pagination
              count={Math.ceil(pagination.total/pagination.per_page)}
              onChange={handlePageChanged}
              page={pagination.page}
              shape="rounded"
            />
          </Stack>
        }
        {showDeleteConfirmation &&
          <ConfirmationDialog
            confirmText={i18n.t("tag-queries-dialog.remove-confirmation.confirm-text")}
            description={i18n.t("tag-queries-dialog.remove-confirmation.description")}
            isOpen={showDeleteConfirmation}
            onClose={(confirmed) => {
              if (confirmed && selectedQuery.current) {
                removeTagFromQuery(selectedQuery.current)
              }
              setShowDeleteConfirmation(false);
            }}
            title={i18n.t("tag-queries-dialog.remove-confirmation.title")}
          />
        }
      </DialogContent>
      <DialogActions>
        <Button
          color="secondary"
          onClick={(event) => {
            event.stopPropagation();
            props.onClose();
          }}
          size="small"
          variant="contained"
        >
          {i18n.t("close")}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}